import React, {
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import api from '../api';
import Chart from 'react-google-charts';
import { Store } from '../Store';
import { getError, formatEuro } from '../utils';
import LoadingLogo from '../components/general/LoadingLogo';
import MessageBox from '../components/general/MessageBox';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Badge,
  OverlayTrigger,
  Popover,
  Form,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
  Modal,
} from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import { toast } from 'react-toastify';
import { formatDate } from '../utils';
import { getSalesDateRange, mergeDailySales } from '../utils/salesChart';
import { addDaysToDateOnly } from '../utils/holidayDates';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true };
    case 'FETCH_SUCCESS':
      return {
        ...state,
        loading: false,
      };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
};

export default function AdminDashboardScreen() {
  const [{ loading, error }, dispatch] = useReducer(reducer, {
    loading: true, // true, because i send an axios request at the beginning of loading this page
    error: '',
  });
  const navigate = useNavigate();
  const { state } = useContext(Store); // gets the application state
  const { userInfo } = state;

  const [loadingMaintenanceMode, setLoadingMaintenanceMode] = useState(true);
  const [enabledMaintenanceMode, setEnabledMaintenanceMode] = useState(false);
  const [submittingMaintenanceMode, setSubmittingMaintenanceMode] =
    useState(false);

  const [loadingOrderingSetting, setLoadingOrderingSetting] = useState(true);
  const [disabledOrderingSetting, setDisabledOrderingSetting] = useState(false);
  const [submittingOrderingSetting, setSubmittingOrderingSetting] =
    useState(false);

  const [loadingHolidaySetting, setLoadingHolidaySetting] = useState(true);
  const [holidaySetting, setHolidaySetting] = useState({
    enabled: false,
    startDate: '',
    endDate: '',
  });
  const [holidayStartDate, setHolidayStartDate] = useState('');
  const [holidayEndDate, setHolidayEndDate] = useState('');
  const [showHolidayModal, setShowHolidayModal] = useState(false);
  const [submittingHolidaySetting, setSubmittingHolidaySetting] =
    useState(false);

  const [summary, setSummary] = useState({
    verifiedUsers: 0,
    unverifiedUsers: 0,
    orders: [],
    dailyOrders: [],
  });
  const [pendingDesigns, setPendingDesigns] = useState(0);
  const [totalDesigns, setTotalDesigns] = useState(0);
  const [pendingAndProcessingOrders, setPendingAndProcessingOrders] =
    useState(0);

  const options = {
    backgroundColor: '#ffffff', // Set the background color to black
    colors: [
      '#00be0e',
      '#00d116',
      '#00a70b',
      '#1bce21',
      '#00a50d',
      '#29d42f',
      '#008c0a',
      '#00c915',
      '#007f08',
      '#14b719',
    ], // Sets the colors of the charts conents
    titleTextStyle: {
      color: '#000000', // Set the text color to white
    },
    legend: {
      textStyle: {
        color: '#000000', // Set the legend text color to white
      },
    },
    hAxis: {
      textStyle: {
        color: '#000000', // Set the horizontal axis text color to white
      },
      gridlines: {
        color: '#000000', // Set the horizontal axis gridlines color to white
      },
    },
    vAxis: {
      textStyle: {
        color: '#000000', // Set the vertical axis text color to white
      },
      gridlines: {
        color: '#000000', // Set the vertical axis gridlines color to white
      },
    },
  }; // opject that holds the styling for the Chart components

  useEffect(() => {
    api
      .get('/api/settings/maintenance')
      .then(({ data }) => {
        setEnabledMaintenanceMode(!!data.maintenance);
      })
      .finally(() => setLoadingMaintenanceMode(false));
  }, []); // fetch current maintenance flag once

  useEffect(() => {
    api
      .get('/api/settings/ordering')
      .then(({ data }) => {
        setDisabledOrderingSetting(!!data.orderingDisabled);
      })
      .finally(() => setLoadingOrderingSetting(false));
  }, []); // fetch current ordering disabled flag once

  useEffect(() => {
    api
      .get('/api/settings/holiday-admin')
      .then(({ data }) => {
        setHolidaySetting({
          enabled: !!data.enabled,
          startDate: data.startDate || '',
          endDate: data.endDate || '',
        }); // save the current holiday configuration

        setHolidayStartDate(data.startDate || ''); // fill the holiday start date input
        setHolidayEndDate(data.endDate || ''); // fill the holiday end date input
      })
      .catch((err) => {
        console.error('Failed to fetch holiday setting:', err);
      })
      .finally(() => {
        setLoadingHolidaySetting(false); // finish loading after the request completes
      });
  }, []); // fetches the current holiday configuration once when the component loads

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await api.get('/api/orders/summary');

        const verifiedUsers =
          data.userSummary.find((item) => item.isVerified)?.count || 0;
        const unverifiedUsers =
          data.userSummary.find((item) => !item.isVerified)?.count || 0;
        setSummary({
          verifiedUsers,
          unverifiedUsers,
          orders: data.orders,
          dailyOrders: data.dailyOrders,
        });

        dispatch({ type: 'FETCH_SUCCESS' });
      } catch (err) {
        dispatch({
          type: 'FETCH_FAIL',
          payload: getError(err),
        });
      }
    }; // fetches a summarized version of the order data from the backend, when it's called

    const fetchPendingCount = async () => {
      try {
        const [designsResponse, ordersResponse] = await Promise.all([
          api.get('/api/designs/count/documents'),
          api.get('/api/orders/count/pendingAndProcessing'),
        ]);

        setPendingDesigns(designsResponse.data.count);
        setTotalDesigns(designsResponse.data.total);
        setPendingAndProcessingOrders(
          ordersResponse.data.pendingCount + ordersResponse.data.processingCount
        );
      } catch (error) {
        console.error('Error fetching pending designs count:', error);
      }
    };

    fetchData();
    fetchPendingCount();
  }, [userInfo]);

  const [selectedRange, setSelectedRange] = useState('currentMonth'); // the range button of the "Bruttoumsatzdiagramm"
  const { startDate, endDate } = useMemo(
    () => getSalesDateRange(selectedRange),
    [selectedRange]
  ); // its period, computed when a button is pressed

  const mergedData = useMemo(
    () => mergeDailySales(summary.dailyOrders, startDate, endDate),
    [summary.dailyOrders, startDate, endDate]
  ); // the chart's rows, one per day of the range; summary always holds dailyOrders (its initial state has an empty list)

  const askAndToggleMaintenance = async () => {
    const action = enabledMaintenanceMode
      ? 'turn OFF maintenance mode'
      : 'turn ON maintenance mode';
    const ok = window.confirm(`Are you sure you want to ${action}?`);
    if (!ok) return;

    try {
      setSubmittingMaintenanceMode(true);
      const { data } = await api.put('/api/settings/set-maintenance-mode', {
        enabled: !enabledMaintenanceMode,
      });

      setEnabledMaintenanceMode(data.enabled);
      toast.success(
        data.enabled
          ? 'Maintenance mode is now ON.'
          : 'Maintenance mode is now OFF.'
      );
    } catch (err) {
      toast.error('Failed to toggle maintenance mode.');
      console.error(err);
    } finally {
      setSubmittingMaintenanceMode(false);
    }
  }; // calling this function toggles the maintanaceMode

  const askAndToggleOrdering = async () => {
    const action = disabledOrderingSetting
      ? 'enable ordering'
      : 'disable ordering';
    const ok = window.confirm(`Are you sure you want to ${action}?`);
    if (!ok) return;

    try {
      setSubmittingOrderingSetting(true);
      const { data } = await api.put('/api/settings/set-disable-ordering', {
        enabled: !disabledOrderingSetting,
      });

      setDisabledOrderingSetting(data.enabled);
      toast.success(
        data.enabled ? 'Ordering is now disabled' : 'Ordering is now enabled'
      );
    } catch (err) {
      toast.error('Failed to toggle disable ordering setting');
      console.error(err);
    } finally {
      setSubmittingOrderingSetting(false);
    }
  }; // calling this function toggles the place order permission setting

  const holidayBannerStartDate = addDaysToDateOnly(holidayStartDate, -14); // calculate when the holiday banner should become visible (empty without the date)

  const holidayOrderDeferralDate = addDaysToDateOnly(holidayStartDate, -7); // calculate when new orders should be processed after the holiday (empty without the date)

  const holidayProcessingResumeDate = addDaysToDateOnly(holidayEndDate, 1); // calculate when processing and manufacturing resume after the holiday (empty without the date)

  const openHolidayModal = () => {
    // reset the form fields to the currently saved dates:
    setHolidayStartDate(holidaySetting.startDate || '');
    setHolidayEndDate(holidaySetting.endDate || '');

    setShowHolidayModal(true);
  }; // opens the holiday modal with the currently saved holiday dates

  const saveHolidayPeriod = async () => {
    if (!holidayStartDate || !holidayEndDate) {
      toast.error('Please select a holiday start and end date.');
      return;
    }

    if (holidayEndDate < holidayStartDate) {
      toast.error('The holiday end date cannot be before the start date.');
      return;
    }

    const ok = window.confirm(
      `Activate holiday period from ${holidayStartDate} to ${holidayEndDate}?`
    ); // ask the admin to confirm the selected holiday period

    if (!ok) {
      return;
    }

    try {
      setSubmittingHolidaySetting(true);

      const { data } = await api.put('/api/settings/set-holiday-period', {
        enabled: true,
        startDate: holidayStartDate,
        endDate: holidayEndDate,
      }); // save and enable the selected holiday period

      setHolidaySetting({
        enabled: data.enabled,
        startDate: data.startDate,
        endDate: data.endDate,
      }); // update the saved holiday configuration in the frontend

      setHolidayStartDate(data.startDate);
      setHolidayEndDate(data.endDate);

      setShowHolidayModal(false);

      toast.success('Holiday period has been activated.');
    } catch (err) {
      console.error(err);
      toast.error(
        err.response?.data?.message || 'Failed to save holiday period.'
      );
    } finally {
      setSubmittingHolidaySetting(false);
    }
  }; // validates, saves and activates the selected holiday period

  const disableHolidayPeriod = async () => {
    const ok = window.confirm(
      'Are you sure you want to disable the holiday notice?'
    ); // ask the admin to confirm disabling the holiday setting

    if (!ok) {
      return;
    }

    try {
      setSubmittingHolidaySetting(true);

      const { data } = await api.put('/api/settings/set-holiday-period', {
        enabled: false,
      }); // disable the holiday setting without changing the saved dates

      setHolidaySetting((current) => ({
        ...current,
        enabled: false,
        startDate: data.startDate || current.startDate,
        endDate: data.endDate || current.endDate,
      })); // keep the previously saved dates so they can be reused later

      setShowHolidayModal(false);

      toast.success('Holiday period has been disabled.');
    } catch (err) {
      console.error(err);
      toast.error('Failed to disable holiday period.');
    } finally {
      setSubmittingHolidaySetting(false);
    }
  }; // disables the holiday setting while keeping the previously selected dates

  return (
    <Container>
      <div>
        <title>Admin Dashboard</title>

        <h1 className="screen-heading">Admin Dashboard</h1>

        {loading || loadingMaintenanceMode || loadingOrderingSetting ? (
          <div className="overlay">
            <LoadingLogo />
          </div>
        ) : error ? (
          <MessageBox className="danger-alert">{error}</MessageBox>
        ) : (
          <>
            <Modal
              show={showHolidayModal}
              onHide={() => {
                if (!submittingHolidaySetting) {
                  setShowHolidayModal(false);
                }
              }}
              className="holiday-modal"
              centered
            >
              <Modal.Header closeButton>
                <Modal.Title>Holiday Period</Modal.Title>
              </Modal.Header>

              <Modal.Body>
                {holidaySetting.enabled && (
                  <Alert
                    variant="warning"
                    style={{ fontSize: '1.4rem', textAlign: 'center' }}
                  >
                    The holiday notice is currently active.
                  </Alert>
                )}

                <div className="d-flex">
                  <Form.Group
                    className="mb-3 w-50"
                    style={{
                      marginRight: '0.5rem',
                    }}
                  >
                    <Form.Label style={{ fontWeight: 'bold' }}>
                      Holiday begins
                    </Form.Label>

                    <Form.Control
                      type="date"
                      value={holidayStartDate}
                      onChange={(event) =>
                        setHolidayStartDate(event.target.value)
                      }
                    />
                  </Form.Group>

                  <Form.Group
                    className="mb-3 w-50"
                    style={{
                      marginLeft: '0.5rem',
                    }}
                  >
                    <Form.Label style={{ fontWeight: 'bold' }}>
                      Holiday ends
                    </Form.Label>

                    <Form.Control
                      type="date"
                      value={holidayEndDate}
                      onChange={(event) =>
                        setHolidayEndDate(event.target.value)
                      }
                    />
                  </Form.Group>
                </div>

                {holidayStartDate &&
                  holidayEndDate &&
                  holidayEndDate >= holidayStartDate && (
                    <Alert
                      variant="info"
                      className="mt-4"
                      style={{ fontSize: '1.4rem' }}
                    >
                      <h4 style={{ fontWeight: 'bold' }}>Automatic schedule</h4>

                      <hr />

                      <div>
                        Holiday banner becomes visible:
                        <br />
                        <strong>
                          {formatDate(holidayBannerStartDate, {
                            dateOnly: true,
                            locale: 'de-DE',
                          })}
                        </strong>
                      </div>

                      <br />

                      <div>
                        Orders from this date may ship after the holiday:
                        <br />
                        <strong>
                          {formatDate(holidayOrderDeferralDate, {
                            dateOnly: true,
                            locale: 'de-DE',
                          })}
                        </strong>
                      </div>

                      <br />

                      <div>
                        Holiday period:
                        <br />
                        <strong>
                          {formatDate(holidayStartDate, {
                            dateOnly: true,
                            locale: 'de-DE',
                          })}{' '}
                          –{' '}
                          {formatDate(holidayEndDate, {
                            dateOnly: true,
                            locale: 'de-DE',
                          })}
                        </strong>
                      </div>

                      <br />

                      <div>
                        Processing and manufacturing resume:
                        <br />
                        <strong>
                          {formatDate(holidayProcessingResumeDate, {
                            dateOnly: true,
                            locale: 'de-DE',
                          })}
                        </strong>
                      </div>
                    </Alert>
                  )}
              </Modal.Body>

              <Modal.Footer>
                {holidaySetting.enabled && (
                  <Button
                    variant="danger"
                    onClick={disableHolidayPeriod}
                    className="mx-2"
                    disabled={submittingHolidaySetting}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="currentColor"
                      className="bi bi-trash"
                      style={{ marginRight: '0.7rem' }}
                      viewBox="0 0 16 16"
                    >
                      <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0z" />
                      <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4zM2.5 3h11V2h-11z" />
                    </svg>
                    Disable
                  </Button>
                )}

                <Button
                  variant="secondary"
                  onClick={saveHolidayPeriod}
                  className="mx-2"
                  disabled={
                    submittingHolidaySetting ||
                    !holidayStartDate ||
                    !holidayEndDate ||
                    holidayEndDate < holidayStartDate
                  }
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="currentColor"
                    className="bi bi-floppy"
                    style={{ marginRight: '0.7rem' }}
                    viewBox="0 0 16 16"
                  >
                    <path d="M11 2H9v3h2z" />
                    <path d="M1.5 0h11.586a1.5 1.5 0 0 1 1.06.44l1.415 1.414A1.5 1.5 0 0 1 16 2.914V14.5a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 0 14.5v-13A1.5 1.5 0 0 1 1.5 0M1 1.5v13a.5.5 0 0 0 .5.5H2v-4.5A1.5 1.5 0 0 1 3.5 9h9a1.5 1.5 0 0 1 1.5 1.5V15h.5a.5.5 0 0 0 .5-.5V2.914a.5.5 0 0 0-.146-.353l-1.415-1.415A.5.5 0 0 0 13.086 1H13v4.5A1.5 1.5 0 0 1 11.5 7h-7A1.5 1.5 0 0 1 3 5.5V1H1.5a.5.5 0 0 0-.5.5m3 4a.5.5 0 0 0 .5.5h7a.5.5 0 0 0 .5-.5V1H4zM3 15h10v-4.5a.5.5 0 0 0-.5-.5h-9a.5.5 0 0 0-.5.5z" />
                  </svg>
                  {submittingHolidaySetting ? 'Saving...' : 'Save'}
                </Button>
              </Modal.Footer>
            </Modal>

            <div className="scrollable-container">
              <div className="card-container">
                <Card className="admin-dashboard-button-card">
                  <Card.Body
                    className="dashboard-management-card"
                    onClick={() => navigate(`/admin/orders`)}
                  >
                    <Badge className="notification-badge-card">
                      {pendingAndProcessingOrders > 0
                        ? pendingAndProcessingOrders
                        : null}
                    </Badge>
                    <div className="d-flex justify-content-center w-100">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="32"
                        height="32"
                        fill="currentColor"
                        className="bi bi-cart-check"
                        viewBox="0 0 16 16"
                      >
                        <path d="M11.354 6.354a.5.5 0 0 0-.708-.708L8 8.293 6.854 7.146a.5.5 0 1 0-.708.708l1.5 1.5a.5.5 0 0 0 .708 0z" />
                        <path d="M.5 1a.5.5 0 0 0 0 1h1.11l.401 1.607 1.498 7.985A.5.5 0 0 0 4 12h1a2 2 0 1 0 0 4 2 2 0 0 0 0-4h7a2 2 0 1 0 0 4 2 2 0 0 0 0-4h1a.5.5 0 0 0 .491-.408l1.5-8A.5.5 0 0 0 14.5 3H2.89l-.405-1.621A.5.5 0 0 0 2 1zm3.915 10L3.102 4h10.796l-1.313 7zM6 14a1 1 0 1 1-2 0 1 1 0 0 1 2 0m7 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0" />
                      </svg>
                    </div>
                    <div className="dashboard-container">
                      <Card.Title className="dashboard-management-title">
                        Orders
                      </Card.Title>
                    </div>
                  </Card.Body>
                </Card>
              </div>
              <div className="card-container">
                <Card className="admin-dashboard-button-card">
                  <Card.Body
                    className="dashboard-management-card"
                    onClick={() => navigate(`/admin/designs`)}
                  >
                    <Badge className="notification-badge-card">
                      {pendingDesigns > 0 ? pendingDesigns : null}
                    </Badge>

                    <div className="d-flex justify-content-center w-100">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="32"
                        height="32"
                        fill="currentColor"
                        className="bi bi-brush"
                        viewBox="0 0 16 16"
                      >
                        <path d="M15.825.12a.5.5 0 0 1 .132.584c-1.53 3.43-4.743 8.17-7.095 10.64a6.1 6.1 0 0 1-2.373 1.534c-.018.227-.06.538-.16.868-.201.659-.667 1.479-1.708 1.74a8.1 8.1 0 0 1-3.078.132 4 4 0 0 1-.562-.135 1.4 1.4 0 0 1-.466-.247.7.7 0 0 1-.204-.288.62.62 0 0 1 .004-.443c.095-.245.316-.38.461-.452.394-.197.625-.453.867-.826.095-.144.184-.297.287-.472l.117-.198c.151-.255.326-.54.546-.848.528-.739 1.201-.925 1.746-.896q.19.012.348.048c.062-.172.142-.38.238-.608.261-.619.658-1.419 1.187-2.069 2.176-2.67 6.18-6.206 9.117-8.104a.5.5 0 0 1 .596.04M4.705 11.912a1.2 1.2 0 0 0-.419-.1c-.246-.013-.573.05-.879.479-.197.275-.355.532-.5.777l-.105.177c-.106.181-.213.362-.32.528a3.4 3.4 0 0 1-.76.861c.69.112 1.736.111 2.657-.12.559-.139.843-.569.993-1.06a3 3 0 0 0 .126-.75zm1.44.026c.12-.04.277-.1.458-.183a5.1 5.1 0 0 0 1.535-1.1c1.9-1.996 4.412-5.57 6.052-8.631-2.59 1.927-5.566 4.66-7.302 6.792-.442.543-.795 1.243-1.042 1.826-.121.288-.214.54-.275.72v.001l.575.575zm-4.973 3.04.007-.005zm3.582-3.043.002.001h-.002z" />
                      </svg>
                    </div>

                    <div className="dashboard-container">
                      <Card.Title>Designs</Card.Title>
                    </div>

                    <span className="total-designs-card-count">
                      {totalDesigns > 0 ? totalDesigns : null}
                    </span>
                  </Card.Body>
                </Card>
              </div>
              <div className="card-container">
                <Card className="admin-dashboard-button-card">
                  <Card.Body
                    className="dashboard-management-card"
                    onClick={() => navigate(`/admin/stock`)}
                  >
                    <div className="d-flex justify-content-center w-100">
                      <svg
                        fill="#000000"
                        height="32px"
                        width="32px"
                        version="1.1"
                        id="Capa_1"
                        viewBox="0 0 297.00 297.00"
                        stroke="#000000"
                        strokeWidth="0.00297"
                      >
                        <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                        <g
                          id="SVGRepo_tracerCarrier"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        ></g>
                        <g id="SVGRepo_iconCarrier">
                          <g>
                            <path d="M291.219,78.753L153.176,9.732c-2.943-1.473-6.409-1.473-9.353,0L5.781,78.753C2.238,80.525,0,84.145,0,88.107v189.808 c0,5.775,4.682,10.458,10.458,10.458h34.511c5.775,0,10.458-4.682,10.458-10.458V115.82h186.148v162.095 c0,5.775,4.682,10.458,10.458,10.458h34.511c5.775,0,10.458-4.682,10.458-10.458V88.107C297,84.145,294.762,80.525,291.219,78.753z M276.085,267.457h-13.595V105.362c0-5.775-4.682-10.458-10.458-10.458H44.968c-5.775,0-10.458,4.682-10.458,10.458v162.095H20.915 V94.57L148.5,30.778L276.085,94.57V267.457z"></path>{' '}
                            <path d="M217.521,132.552H148.5c-4.043,0-7.32,3.277-7.32,7.32v61.701H79.479c-4.043,0-7.32,3.277-7.32,7.32v69.021 c0,4.043,3.277,7.32,7.32,7.32h138.042c4.043,0,7.32-3.277,7.32-7.32V139.873C224.842,135.829,221.564,132.552,217.521,132.552z M155.82,226.568l16.837,16.837l-16.837,16.837V226.568z M141.18,260.241l-16.837-16.837l16.837-16.837V260.241z M166.174,216.214 h33.673l-16.837,16.837L166.174,216.214z M166.174,201.573l16.837-16.837l16.837,16.837H166.174z M210.201,191.22l-16.836-16.837 l16.836-16.837V191.22z M183.011,164.03l-16.837-16.837h33.673L183.011,164.03z M172.657,174.383L155.82,191.22v-33.673 L172.657,174.383z M113.989,233.051l-16.837-16.837h33.673L113.989,233.051z M103.636,243.404l-16.837,16.837v-33.673 L103.636,243.404z M113.989,253.758l16.837,16.837H97.153L113.989,253.758z M183.01,253.758l16.837,16.837h-33.673L183.01,253.758z M193.364,243.404l16.837-16.837v33.673L193.364,243.404z"></path>{' '}
                          </g>
                        </g>
                      </svg>
                    </div>
                    <div className="dashboard-container">
                      <Card.Title>Stock</Card.Title>
                    </div>
                  </Card.Body>
                </Card>
              </div>
              <div className="card-container">
                <Card className="admin-dashboard-button-card">
                  <Card.Body
                    className="dashboard-management-card"
                    onClick={() => navigate(`/admin/users`)}
                  >
                    <div className="d-flex justify-content-center w-100">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="32"
                        height="32"
                        fill="currentColor"
                        className="bi bi-people"
                        viewBox="0 0 16 16"
                      >
                        <path d="M15 14s1 0 1-1-1-4-5-4-5 3-5 4 1 1 1 1zm-7.978-1L7 12.996c.001-.264.167-1.03.76-1.72C8.312 10.629 9.282 10 11 10c1.717 0 2.687.63 3.24 1.276.593.69.758 1.457.76 1.72l-.008.002-.014.002zM11 7a2 2 0 1 0 0-4 2 2 0 0 0 0 4m3-2a3 3 0 1 1-6 0 3 3 0 0 1 6 0M6.936 9.28a6 6 0 0 0-1.23-.247A7 7 0 0 0 5 9c-4 0-5 3-5 4q0 1 1 1h4.216A2.24 2.24 0 0 1 5 13c0-1.01.377-2.042 1.09-2.904.243-.294.526-.569.846-.816M4.92 10A5.5 5.5 0 0 0 4 13H1c0-.26.164-1.03.76-1.724.545-.636 1.492-1.256 3.16-1.275ZM1.5 5.5a3 3 0 1 1 6 0 3 3 0 0 1-6 0m3-2a2 2 0 1 0 0 4 2 2 0 0 0 0-4" />
                      </svg>
                    </div>
                    <div className="dashboard-container">
                      <Card.Title>Users</Card.Title>
                    </div>
                  </Card.Body>
                </Card>
              </div>
              <div className="card-container">
                <Card className="admin-dashboard-button-card">
                  <Card.Body
                    className="dashboard-management-card"
                    onClick={() => navigate(`/admin/products`)}
                  >
                    <div className="d-flex justify-content-center w-100">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="32"
                        height="32"
                        fill="currentColor"
                        className="bi bi-box-seam"
                        viewBox="0 0 16 16"
                      >
                        <path d="M8.186 1.113a.5.5 0 0 0-.372 0L1.846 3.5l2.404.961L10.404 2zm3.564 1.426L5.596 5 8 5.961 14.154 3.5zm3.25 1.7-6.5 2.6v7.922l6.5-2.6V4.24zM7.5 14.762V6.838L1 4.239v7.923zM7.443.184a1.5 1.5 0 0 1 1.114 0l7.129 2.852A.5.5 0 0 1 16 3.5v8.662a1 1 0 0 1-.629.928l-7.185 2.874a.5.5 0 0 1-.372 0L.63 13.09a1 1 0 0 1-.63-.928V3.5a.5.5 0 0 1 .314-.464z" />
                      </svg>
                    </div>
                    <div className="dashboard-container">
                      <Card.Title>Products</Card.Title>
                    </div>
                  </Card.Body>
                </Card>
              </div>
              <div className="card-container">
                <Card className="admin-dashboard-button-card">
                  <Card.Body
                    className="dashboard-management-card"
                    onClick={() => navigate(`/admin/layouts`)}
                  >
                    <div className="d-flex justify-content-center w-100">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="32"
                        height="32"
                        fill="currentColor"
                        className="bi bi-grid-3x2-gap"
                        viewBox="0 0 16 16"
                      >
                        <path d="M4 4v2H2V4zm1 7V9a1 1 0 0 0-1-1H2a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1m0-5V4a1 1 0 0 0-1-1H2a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1m5 5V9a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1m0-5V4a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1M9 4v2H7V4zm5 0h-2v2h2zM4 9v2H2V9zm5 0v2H7V9zm5 0v2h-2V9zm-3-5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1zm1 4a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1V9a1 1 0 0 0-1-1z" />
                      </svg>
                    </div>
                    <div className="dashboard-container">
                      <Card.Title>Layouts</Card.Title>
                    </div>
                  </Card.Body>
                </Card>
              </div>
              <div className="card-container">
                <Card className="admin-dashboard-button-card">
                  <Card.Body
                    className="dashboard-management-card"
                    onClick={() => navigate(`/admin/legends`)}
                  >
                    <div className="d-flex justify-content-center w-100">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="32"
                        height="32"
                        fill="currentColor"
                        className="bi bi-fonts"
                        viewBox="0 0 16 16"
                      >
                        <path d="M12.258 3h-8.51l-.083 2.46h.479c.26-1.544.758-1.783 2.693-1.845l.424-.013v7.827c0 .663-.144.82-1.3.923v.52h4.082v-.52c-1.162-.103-1.306-.26-1.306-.923V3.602l.431.013c1.934.062 2.434.301 2.693 1.846h.479z" />
                      </svg>
                    </div>
                    <div className="dashboard-container">
                      <Card.Title>Legends</Card.Title>
                    </div>
                  </Card.Body>
                </Card>
              </div>
              <div className="card-container">
                <Card className="admin-dashboard-button-card">
                  <Card.Body
                    className="dashboard-management-card"
                    onClick={() => navigate(`/admin/molds`)}
                  >
                    <div className="d-flex justify-content-center w-100">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="40"
                        height="40"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fill="currentColor"
                          d="M5 6a1 1 0 0 0-1 1v1.5a1 1 0 0 0 1 1h3.5a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1zm3.5 1v1.5H5V7zM4 11.5a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1V13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1zm1 0V13h3.5v-1.5zM11.5 6H15a1 1 0 0 1 1 1v1.5a1 1 0 0 1-1 1h-3.5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1M15 7h-3.5v1.5H15zm-4.5 4.5a1 1 0 0 1 1-1H15a1 1 0 0 1 1 1V13a1 1 0 0 1-1 1h-3.5a1 1 0 0 1-1-1zm1 0V13H15v-1.5zM2 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1z"
                        />
                      </svg>
                    </div>
                    <div className="dashboard-container">
                      <Card.Title>Molds</Card.Title>
                    </div>
                  </Card.Body>
                </Card>
              </div>
              <div className="card-container">
                <Card className="admin-dashboard-button-card">
                  <Card.Body
                    className="dashboard-management-card"
                    onClick={() => navigate(`/admin/fonts`)}
                  >
                    <div className="d-flex justify-content-center w-100">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="32"
                        height="32"
                        fill="currentColor"
                        className="bi bi-filetype-ttf"
                        viewBox="0 0 16 16"
                      >
                        <path
                          fillRule="evenodd"
                          d="M14 4.5V14a2 2 0 0 1-2 2h-2v-1h2a1 1 0 0 0 1-1V4.5h-2A1.5 1.5 0 0 1 9.5 3V1H4a1 1 0 0 0-1 1v9H2V2a2 2 0 0 1 2-2h5.5zM1.928 15.849v-3.337h2.269v3.337h.794v-3.337h1.137v-.662H0v.662h1.134v3.337zm5.315-1.59v1.59h-.791V11.85H9v.653H7.243v1.117h1.605v.638z"
                        />
                      </svg>
                    </div>
                    <div className="dashboard-container">
                      <Card.Title>Fonts</Card.Title>
                    </div>
                  </Card.Body>
                </Card>
              </div>
              <div className="card-container">
                <Card className="admin-dashboard-button-card">
                  <Card.Body
                    className="dashboard-management-card"
                    onClick={() => navigate(`/admin/zones`)}
                  >
                    <div className="d-flex justify-content-center w-100">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="34"
                        height="34"
                        fill="currentColor"
                        className="bi bi-truck"
                        viewBox="0 0 16 16"
                      >
                        <path d="M0 3.5A1.5 1.5 0 0 1 1.5 2h9A1.5 1.5 0 0 1 12 3.5V5h1.02a1.5 1.5 0 0 1 1.17.563l1.481 1.85a1.5 1.5 0 0 1 .329.938V10.5a1.5 1.5 0 0 1-1.5 1.5H14a2 2 0 1 1-4 0H5a2 2 0 1 1-3.998-.085A1.5 1.5 0 0 1 0 10.5zm1.294 7.456A2 2 0 0 1 4.732 11h5.536a2 2 0 0 1 .732-.732V3.5a.5.5 0 0 0-.5-.5h-9a.5.5 0 0 0-.5.5v7a.5.5 0 0 0 .294.456M12 10a2 2 0 0 1 1.732 1h.768a.5.5 0 0 0 .5-.5V8.35a.5.5 0 0 0-.11-.312l-1.48-1.85A.5.5 0 0 0 13.02 6H12zm-9 1a1 1 0 1 0 0 2 1 1 0 0 0 0-2m9 0a1 1 0 1 0 0 2 1 1 0 0 0 0-2" />
                      </svg>
                    </div>
                    <div className="dashboard-container">
                      <Card.Title className="dashboard-management-title">
                        Zones
                      </Card.Title>
                    </div>
                  </Card.Body>
                </Card>
              </div>
              <div className="card-container">
                <Card className="admin-dashboard-button-card">
                  <Card.Body
                    className="dashboard-management-card"
                    onClick={() => navigate(`/admin/colors`)}
                  >
                    <div className="d-flex justify-content-center w-100">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="32"
                        height="32"
                        fill="currentColor"
                        className="bi bi-palette"
                        viewBox="0 0 16 16"
                      >
                        <path d="M8 5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3m4 3a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3M5.5 7a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0m.5 6a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3" />
                        <path d="M16 8c0 3.15-1.866 2.585-3.567 2.07C11.42 9.763 10.465 9.473 10 10c-.603.683-.475 1.819-.351 2.92C9.826 14.495 9.996 16 8 16a8 8 0 1 1 8-8m-8 7c.611 0 .654-.171.655-.176.078-.146.124-.464.07-1.119-.014-.168-.037-.37-.061-.591-.052-.464-.112-1.005-.118-1.462-.01-.707.083-1.61.704-2.314.369-.417.845-.578 1.272-.618.404-.038.812.026 1.16.104.343.077.702.186 1.025.284l.028.008c.346.105.658.199.953.266.653.148.904.083.991.024C14.717 9.38 15 9.161 15 8a7 7 0 1 0-7 7" />
                      </svg>
                    </div>
                    <div className="dashboard-container">
                      <Card.Title className="dashboard-management-title">
                        Colors
                      </Card.Title>
                    </div>
                  </Card.Body>
                </Card>
              </div>
            </div>

            <Row>
              <Col md={4}>
                <Card>
                  <Card.Body className="dashboard-card">
                    {/* Card.Body creates padding for its content */}
                    <div>
                      <div className="d-flex">
                        <Card.Title className="dashboard-title">
                          {summary.verifiedUsers ? summary.verifiedUsers : 0}
                        </Card.Title>
                        <Card.Text>Verifizierte Benutzer</Card.Text>
                      </div>
                      <div
                        className="d-flex justify-content-center"
                        style={{
                          fontSize: '10px',
                          color:
                            summary.unverifiedUsers <= 0 ? 'grey' : 'black',
                        }}
                      >
                        (
                        <span
                          style={{
                            paddingRight: '3px',
                            color:
                              summary.unverifiedUsers <= 0 ? 'grey' : 'red',
                          }}
                        >
                          {summary.unverifiedUsers
                            ? summary.unverifiedUsers
                            : 0}
                        </span>
                        Unverifizierte Benutzer)
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card style={{ height: '100%' }}>
                  <Card.Body className="dashboard-card">
                    <Card.Title className="dashboard-title">
                      {summary.orders && summary.orders.length
                        ? summary.orders[0].totalQuantity
                        : 0}
                    </Card.Title>
                    <Card.Text>Verkaufte Keycap-Sets</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card style={{ height: '100%' }}>
                  <Card.Body className="dashboard-card">
                    <Card.Title className="dashboard-title">
                      {summary.orders && summary.orders.length
                        ? summary.orders[0].numOrders
                        : 0}
                    </Card.Title>
                    <Card.Text>Bestellungen</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row className="my-3 dashboard-card">
              <h2 className="text-center mt-3 mb-5">Bruttoumsatzdiagramm</h2>
              {summary.dailyOrders.length === 0 ? (
                <MessageBox>Bislang liegen keine Verkaufsdaten vor</MessageBox>
              ) : (
                <Chart
                  width="100%"
                  height="400px"
                  chartType="AreaChart"
                  options={options}
                  loader={<div>Loading Chart...</div>}
                  data={[['Datum', 'Bruttoumsatz'], ...mergedData]}
                /> // react-google-chart component
              )}
            </Row>

            <Row>
              <Form.Group
                controlId="groupSelect"
                className="d-flex justify-content-end"
              >
                <ToggleButtonGroup
                  type="radio"
                  name="selectedGroup"
                  value={selectedRange}
                  onChange={setSelectedRange}
                  style={{ marginRight: '5px' }}
                >
                  <ToggleButton
                    key="currentMonth"
                    id="currentMonth"
                    value="currentMonth"
                    variant="outline-secondary"
                  >
                    Current Month
                  </ToggleButton>
                  <ToggleButton
                    key="lastMonth"
                    id="lastMonth"
                    value="lastMonth"
                    variant="outline-secondary"
                  >
                    Last Month
                  </ToggleButton>
                  <ToggleButton
                    key="currentYear"
                    id="currentYear"
                    value="currentYear"
                    variant="outline-secondary"
                  >
                    Current Year
                  </ToggleButton>
                  <ToggleButton
                    key="lastYear"
                    id="lastYear"
                    value="lastYear"
                    variant="outline-secondary"
                  >
                    Last Year
                  </ToggleButton>
                  <ToggleButton
                    key="allTime"
                    id="allTime"
                    value="allTime"
                    variant="outline-secondary"
                  >
                    All Time
                  </ToggleButton>
                </ToggleButtonGroup>
              </Form.Group>
            </Row>

            <Row>
              <Col md={4}>
                <Card>
                  <Card.Body className="dashboard-card">
                    <Card.Title className="dashboard-title">
                      {summary.orders && summary.orders.length
                        ? formatEuro(summary.orders[0].grossRevenue)
                        : formatEuro(0)}
                    </Card.Title>
                    <Card.Text>Bruttoumsatz</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card>
                  <Card.Body className="dashboard-card">
                    <Card.Title className="dashboard-title">
                      {summary.orders && summary.orders.length
                        ? formatEuro(summary.orders[0].netRevenue)
                        : formatEuro(0)}
                    </Card.Title>
                    <Card.Text>Nettoumsatz</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card>
                  <Card.Body className="dashboard-card">
                    <Card.Title className="dashboard-title">
                      {summary.orders && summary.orders.length
                        ? formatEuro(summary.orders[0].totalVat)
                        : formatEuro(0)}
                    </Card.Title>
                    <Card.Text>Mehrwertsteuerbetrag</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <div className="d-flex justify-content-center mt-5">
              <Button
                variant="secondary"
                onClick={openHolidayModal}
                disabled={loadingHolidaySetting || submittingHolidaySetting}
              >
                {holidaySetting.enabled
                  ? 'Edit Holiday Period'
                  : 'Set Holiday Period'}
              </Button>

              <Button
                variant="secondary"
                onClick={askAndToggleOrdering}
                disabled={submittingOrderingSetting}
                className="mx-3"
              >
                {disabledOrderingSetting ? 'Enable' : 'Disable'}&nbsp;Ordering
              </Button>

              <Button
                variant="secondary"
                onClick={askAndToggleMaintenance}
                disabled={submittingMaintenanceMode}
              >
                Turn&nbsp;Maintenance&nbsp;
                <span
                  style={{
                    color: enabledMaintenanceMode ? '#d9534f' : '#28a745',
                  }}
                >
                  {enabledMaintenanceMode ? 'off' : 'on'}
                </span>
              </Button>

              <OverlayTrigger
                trigger="click"
                rootClose // close the popover when the user clicks outside of it
                placement="top"
                overlay={
                  <Popover>
                    <Popover.Body>
                      <strong>
                        Public S3 Buckets and Cookiebot consent banner will not
                        be blocked by maintenance mode!
                      </strong>
                      <br />
                      <br />
                      <p>
                        During an emergency you can manually "Block Public
                        Access settings for this account" in the AWS console for
                        all S3 buckets at once.
                      </p>
                    </Popover.Body>
                  </Popover>
                }
              >
                <Button
                  className="info-popover"
                  style={{ padding: '0px 0px 0px 9px' }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="#707070"
                    className="bi bi-info-circle"
                    viewBox="0 0 16 16"
                  >
                    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                    <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                  </svg>
                </Button>
              </OverlayTrigger>
            </div>
          </>
        )}
      </div>
    </Container>
  );
}
